import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import { useSearchParams } from "react-router-dom";
import { adminApi } from "../../api/admin";
import {
  getPanelPushSubscription,
  subscribePanelPush,
  webPushSupported,
} from "../../utils/webPushClient";

import EditorialIntro from "../../components/dashboard/EditorialIntro";
import ResponsiveListControls from "../../components/dashboard/ResponsiveListControls";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import { useListScrollRestoration, usePersistentListValue } from "../../utils/listViewState";
const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const PAGE_SIZE = 40;

const CATEGORY_LABELS = {
  navigation: "Navegación",
  commercial: "Comercial",
  auth: "Accesos",
  user: "Usuarios",
  project: "Proyectos",
  landing: "Página",
  product: "Productos / servicios",
  order: "Pedidos",
  payment: "Pagos",
  appointment: "Agenda",
  erp: "ERP",
  cash: "Caja",
  stock: "Stock",
  billing: "Billing",
  push: "Notificaciones",
  integration: "Integraciones",
  system: "Sistema",
};

const SEVERITY_COLOR = {
  info: "default",
  success: "success",
  warning: "warning",
  critical: "error",
};

function dateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(date);
}

function Metric({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, "&:last-child": { pb: { xs: 1.5, sm: 2.5 } } }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 900 }}>{title}</Typography>
            <Typography variant="h4" sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.55rem", sm: "2.125rem" }, lineHeight: 1.1 }}>{value}</Typography>
            <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.50)" }}>{helper}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function actorLabel(event) {
  const actor = event?.actor || {};
  if (actor.name) return actor.name;
  if (actor.email) return actor.email;
  if (actor.type === "visitor") return "Visitante web";
  if (actor.type === "customer") return "Cliente registrado";
  if (actor.type === "webhook") return "Webhook";
  return "Sistema";
}

export default function SuperActivityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [journeys, setJourneys] = React.useState([]);
  const [pagination, setPagination] = React.useState({ page: 1, pages: 1, total: 0 });
  const [push, setPush] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [browserEndpoint, setBrowserEndpoint] = React.useState("");
  const [filters, setFilters] = usePersistentListValue("super-activity:filters", {
    category: searchParams.get("category") || "",
    projectSlug: searchParams.get("project") || "",
    severity: "",
    q: "",
  });
  const [page, setPage] = usePersistentListValue("super-activity:page", 1);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [activityResponse, summaryResponse, journeysResponse, pushResponse] = await Promise.all([
        adminApi.activity({
          page,
          pageSize: PAGE_SIZE,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
        }),
        adminApi.activitySummary(24),
        adminApi.activityJourneys({ hours: 168, limit: 20 }),
        adminApi.pushStatus(),
      ]);
      setItems(activityResponse.items || []);
      setPagination(activityResponse.pagination || { page, pages: 1, total: 0 });
      setSummary(summaryResponse.summary || null);
      setJourneys(journeysResponse.items || []);
      setPush(pushResponse);

      if (webPushSupported()) {
        const subscription = await getPanelPushSubscription();
        setBrowserEndpoint(subscription?.endpoint || "");
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar la actividad global");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  React.useEffect(() => {
    load();
  }, [load]);

  useListScrollRestoration("super-activity", !loading);

  const ownEndpoints = new Set((push?.ownSubscriptions || []).map((item) => item.endpoint));
  const globalPushActive = Boolean(browserEndpoint && ownEndpoints.has(browserEndpoint));

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
    const next = new URLSearchParams(searchParams);
    if (key === "category" || key === "projectSlug") {
      const queryKey = key === "projectSlug" ? "project" : key;
      if (value) next.set(queryKey, value);
      else next.delete(queryKey);
      setSearchParams(next, { replace: true });
    }
  };

  const activatePush = async () => {
    try {
      setWorking(true);
      setError("");
      setMessage("");
      const subscription = await subscribePanelPush(push?.publicKey);
      await adminApi.subscribePush(subscription.toJSON(), {
        storefrontActivity: true,
        commercial: true,
        operations: true,
        erp: true,
        security: true,
        panelActivity: true,
        system: true,
        billing: true,
        orders: true,
        payments: true,
        campaigns: false,
      });
      setBrowserEndpoint(subscription.endpoint);
      setMessage("Este navegador ya recibe movimientos globales de WebProfit.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudieron activar las alertas globales");
    } finally {
      setWorking(false);
    }
  };

  const deactivatePush = async () => {
    try {
      setWorking(true);
      setError("");
      setMessage("");
      const subscription = await getPanelPushSubscription();
      if (subscription?.endpoint) await adminApi.unsubscribePush(subscription.endpoint);
      setMessage("Se desvincularon las alertas globales. Las alertas particulares de proyectos no se tocaron.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudieron desactivar las alertas globales");
    } finally {
      setWorking(false);
    }
  };

  const testPush = async () => {
    try {
      setWorking(true);
      setError("");
      const response = await adminApi.testPush();
      const result = response.result || {};
      setMessage(`Prueba procesada: ${result.sent || 0} enviada(s), ${result.failed || 0} fallida(s).`);
      await load();
    } catch (err) {
      setError(err.message || "No se pudo enviar la prueba");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Stack spacing={{ xs: 2, sm: 3 }} sx={{ minWidth: 0 }}>
      <EditorialIntro
        title="Actividad global"
        titleVariant="h3"
        context="Visitas y operaciones de toda la plataforma."
        helpTitle="Qué reúne la actividad global"
        helpText="Combina intención comercial y operaciones realizadas por clientes, equipos, automatizaciones y webhooks para investigar recorridos sin exponer detalle técnico de forma permanente."
        action={<Button onClick={load} disabled={loading} startIcon={<RefreshRoundedIcon />} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Actualizar</Button>}
      />

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={6} lg={3}>
          <Metric icon={<HistoryRoundedIcon />} title="Eventos en 24 h" value={summary?.total ?? "—"} helper="Operación y navegación" />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Metric icon={<PeopleAltRoundedIcon />} title="Visitantes únicos" value={summary?.uniqueVisitors ?? "—"} helper="Identidad anónima por navegador" />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Metric icon={<BoltRoundedIcon />} title="Alta intención" value={summary?.highIntent ?? "—"} helper="WhatsApp, carrito y checkout" />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Metric icon={<NotificationsActiveRoundedIcon />} title="Superadmins conectados" value={push?.subscribers ?? "—"} helper="Navegadores con alertas globales" />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ md: "center" }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Alertas globales</Typography>
              <ContextualHelp compact title="Qué incluye" text="Puede avisar cambios de productos, página, pedidos, pagos, ERP, caja, stock, visitas y acciones comerciales de todos los proyectos." />
            </Stack>
            <Typography sx={{ mt: 0.4, color: "rgba(25,36,47,.62)" }}>
              Avisos operativos de WebProfit en este navegador.
            </Typography>
          </Box>
          <Chip
            icon={globalPushActive ? <NotificationsActiveRoundedIcon /> : <NotificationsOffRoundedIcon />}
            label={globalPushActive ? "Alertas globales activas" : "Alertas globales desactivadas"}
            color={globalPushActive ? "success" : "default"}
            sx={{ fontWeight: 900 }}
          />
        </Stack>
        {!push?.configured && <Alert severity="warning" sx={{ mt: 2 }}>Faltan las variables VAPID del backend.</Alert>}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
          {!globalPushActive ? (
            <Button disabled={working || !push?.configured || !webPushSupported()} onClick={activatePush} startIcon={<NotificationsActiveRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
              Activar alertas globales
            </Button>
          ) : (
            <Button disabled={working} onClick={deactivatePush} startIcon={<NotificationsOffRoundedIcon />} color="warning" variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
              Desvincular alertas globales
            </Button>
          )}
          <Button disabled={working || !globalPushActive} onClick={testPush} startIcon={<SendRoundedIcon />} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
            Enviar prueba
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <RouteRoundedIcon sx={{ color: BRAND.orange }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Recorridos recientes</Typography>
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>Sesiones de visitantes y usuarios del panel, agrupadas paso a paso.</Typography>
          </Box>
        </Stack>
        <Stack spacing={1.25}>
          {journeys.slice(0, 10).map((journey) => (
            <Box key={`${journey.projectId}-${journey.channel}-${journey.sessionId}`} sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB" }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Stack direction="row" spacing={.8} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={journey.channel === "storefront" ? "Web pública" : "Panel"} />
                    {journey.projectName ? <Chip size="small" variant="outlined" label={journey.projectName} /> : null}
                    <Chip size="small" variant="outlined" label={`${journey.stepsCount || 0} pasos`} />
                    {journey.highIntent ? <Chip size="small" color="success" label={`${journey.highIntent} acciones comerciales`} /> : null}
                  </Stack>
                  <Typography variant="body2" sx={{ display: { xs: "block", md: "none" }, mt: 1, color: "rgba(25,36,47,.66)", overflowWrap: "anywhere" }}>
                    {(journey.steps || []).slice(-3).map((step) => step.title || step.action).join(" → ") || "Sin pasos visibles"}
                  </Typography>
                  <Typography variant="body2" sx={{ display: { xs: "none", md: "block" }, mt: 1, color: "rgba(25,36,47,.66)" }}>
                    {(journey.steps || []).slice(-8).map((step) => step.title || step.action).join(" → ") || "Sin pasos visibles"}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)", flexShrink: 0 }}>{dateTime(journey.lastAt)}</Typography>
              </Stack>
            </Box>
          ))}
          {!loading && !journeys.length && <Alert severity="info">Todavía no hay recorridos agrupados.</Alert>}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.1, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <ResponsiveListControls
          search={<TextField fullWidth size="small" placeholder="Buscar actividad" inputProps={{ "aria-label": "Buscar actividad, persona o entidad" }} value={filters.q} onChange={(event) => updateFilter("q", event.target.value)} />}
          activeFilterCount={[filters.category, filters.projectSlug, filters.severity].filter(Boolean).length}
          onClear={() => {
            setPage(1);
            setFilters((current) => ({ ...current, category: "", projectSlug: "", severity: "" }));
            const next = new URLSearchParams(searchParams);
            next.delete("category");
            next.delete("project");
            setSearchParams(next, { replace: true });
          }}
          drawerTitle="Filtrar actividad global"
        >
          <FormControl size="small" sx={{ minWidth: { sm: 180 } }}>
            <InputLabel>Categoría</InputLabel>
            <Select label="Categoría" value={filters.category} onChange={(event) => updateFilter("category", event.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Proyecto / slug" value={filters.projectSlug} onChange={(event) => updateFilter("projectSlug", event.target.value)} sx={{ minWidth: { sm: 180 } }} />
          <FormControl size="small" sx={{ minWidth: { sm: 160 } }}>
            <InputLabel>Severidad</InputLabel>
            <Select label="Severidad" value={filters.severity} onChange={(event) => updateFilter("severity", event.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="info">Información</MenuItem>
              <MenuItem value="success">Éxito</MenuItem>
              <MenuItem value="warning">Advertencia</MenuItem>
              <MenuItem value="critical">Crítica</MenuItem>
            </Select>
          </FormControl>
        </ResponsiveListControls>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Historial</Typography>
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.56)" }}>{pagination.total || 0} eventos encontrados</Typography>
          </Box>
          {loading && <CircularProgress size={24} />}
        </Stack>

        <Stack spacing={1.25}>
          {items.map((event) => (
            <Box key={event.id} sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,.05)" }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
                <Stack direction="row" spacing={1.4} sx={{ minWidth: 0 }}>
                  <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: 2.2, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}>
                    {event.source === "storefront" ? <StorefrontRoundedIcon fontSize="small" /> : <AnalyticsRoundedIcon fontSize="small" />}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{event.title}</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)", mt: 0.25, display: { xs: "-webkit-box", md: "block" }, WebkitLineClamp: { xs: 2, md: "unset" }, WebkitBoxOrient: "vertical", overflow: { xs: "hidden", md: "visible" } }}>{event.description}</Typography>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      <Chip size="small" label={CATEGORY_LABELS[event.category] || event.category} />
                      <Chip size="small" color={SEVERITY_COLOR[event.severity] || "default"} label={event.severity || "info"} />
                      {event.projectName ? <Chip size="small" variant="outlined" label={`${event.projectName}${event.projectSlug ? ` · ${event.projectSlug}` : ""}`} /> : null}
                      <Chip size="small" variant="outlined" label={actorLabel(event)} />
                    </Stack>
                  </Box>
                </Stack>
                <Box sx={{ flexShrink: 0, textAlign: { md: "right" } }}>
                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.52)", fontWeight: 800 }}>{dateTime(event.occurredAt)}</Typography>
                  <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.40)" }}>{event.action}</Typography>
                </Box>
              </Stack>
            </Box>
          ))}
          {!loading && !items.length && <Alert severity="info">No hay actividad para estos filtros.</Alert>}
        </Stack>

        {pagination.pages > 1 && (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Pagination page={pagination.page || page} count={pagination.pages} onChange={(_event, value) => setPage(value)} color="primary" />
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
